"use client";

import { useState, useRef, useCallback, useEffect } from "react";
import Image from "next/image";
import { CategoriesResponse } from "@/types/category";
import CategoryColumn from "./category-column";

interface Props {
  categories: CategoriesResponse[];
}

const NavbarDropdown = ({ categories }: Props) => {
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggleDropdown = useCallback(() => {
    setShowDropdown((prev) => !prev);
  }, []);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      !(event.target as HTMLElement).closest("button")
    ) {
      setShowDropdown(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <div className="transition-all relative inline-block overflow-hidden">
      <button
        onClick={handleToggleDropdown}
        className="border bg-gray-float rounded-md py-4 px-3 mr-2 focus:outline-none focus:bg-gray-100 hover:bg-gray-100 flex items-center"
      >
        Categories
        <span
          className={`ml-3 transition-transform duration-300 ${showDropdown ? "rotate-180" : ""}`}
        >
          <Image
            src="/img/down-arrow.png"
            alt="Dropdown Arrow"
            height={15}
            width={15}
          />
        </span>
      </button>
      {showDropdown && (
        <div
          className="fixed right-[20%] bg-white mt-5 rounded-lg shadow-xl xl:w-[664px] md:w-[615px] sm:w-96 w-[300px]  h-fit overflow-x-auto lg:overflow-hidden z-50"
          ref={dropdownRef}
        >
          <div className="flex flex-col md:flex-row max-w-screen-xl p-5 mx-auto text-gray-900 md:px-6 gap-2">
            <ul className="md:w-1/2">
              {categories
                .slice(0, Math.ceil(categories.length / 2))
                .map((category) => (
                  <CategoryColumn
                    {...category}
                    setShowDropdown={setShowDropdown}
                  />
                ))}
            </ul>
            <div className="border-l border-gray-200 hidden sm:block" />
            <ul className="md:w-1/2">
              {categories
                .slice(Math.ceil(categories.length / 2))
                .map((category) => (
                  <CategoryColumn
                    {...category}
                    setShowDropdown={setShowDropdown}
                  />
                ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavbarDropdown;
